import React from 'react';
import { Builder_, Dropdown_, FormBox, FormBoxSelection, Main_Plain, Slider_, Toggle_Stat } from 'monica-alexandria';
import styled from 'styled-components';
import {
    read_seller_identity_keys,
    read_seller_member_select_value,
    read_seller_name,
    read_seller_options,
    read_seller_select_value
} from '../../../../utils/hr/seller_identity';
import { Brands_Timeline } from './Brands_Timeline';
import { Quest_Provider_Counts } from '../../../../components/quest/Quest_Provider_Counts';

const BrandsSliderContent = styled.div`
    display: grid;
    gap: 2rem;

    .Builder{
        grid-template-columns: 1fr 1fr;
    }
`;

const BrandsSliderSelection = styled(FormBoxSelection)`
    h5 {
        margin-bottom: 0.4rem;
    }

    p {
        margin: 0;
        word-break: break-word;
        white-space: pre-wrap;
    }
`;

const BrandsSliderQuests = styled(FormBox)`
    padding-bottom: 0;
`;

/**
 * Renders one clearly labeled read-only value in the Brand summary.
 */
const BrandsSliderField = ({ label, value }) => {
    return <BrandsSliderSelection>
        <h5>{label}</h5>
        <p>{value}</p>
    </BrandsSliderSelection>;
};

/**
 * Renders the read-only brand details slider.
 */
export default function Brands_Slider(props) {
    const assigneeKeys = read_seller_identity_keys(props?.activeBrandRow?.assignee);
    const currentSellerKeys = read_seller_identity_keys(props?.currentSeller);
    const assigned = assigneeKeys.length > 0;
    const assignedToMe = assigned && assigneeKeys.some((assigneeKey) => currentSellerKeys.includes(assigneeKey));
    const assigning = props?.assigningBrandId === props?.activeBrandRow?.id;
    const assignmentLocked = assigned || assigning || !props?.canAssignToMe;
    const sellerOptions = read_seller_options(props?.teamMembers);
    const savedSeller = props?.activeBrandRow?.assignee;
    const resolvedSellerValue = read_seller_member_select_value(savedSeller, props?.teamMembers);
    const fallbackSellerValue = read_seller_select_value(savedSeller) || assigneeKeys[0] || '';
    const sellerValue = resolvedSellerValue || (assigned ? fallbackSellerValue : '');
    const savedSellerHasOption = sellerOptions.some((option) => option.value === sellerValue);
    const visibleSellerOptions = assigned && sellerValue && !savedSellerHasOption
        ? [{
            key: sellerValue,
            value: sellerValue,
            label: read_seller_name(savedSeller)
                || (typeof savedSeller === 'string' ? savedSeller.trim() : '')
                || props?.t('Assigned seller')
        }, ...sellerOptions]
        : sellerOptions;
    return (
        <Slider_
            title={props?.activeBrandRow?.brand || props?.t('Brand')}
            text={props?.t('Close')}
            onClick={props?.onClose}
            onClose={props?.onClose}
        >
            <BrandsSliderContent>
                {props.canMerge ? <Main_Plain text={props.t('Merge brand')} iconLeft="Group" onClick={props.onMerge}></Main_Plain> : null}
                <Builder_>
                    {props?.canAssignAnySeller ? (
                        <Dropdown_ label={props?.t('Assign to')}>
                            <select
                                disabled={assigned || assigning}
                                value={sellerValue}
                                onChange={(event) => {
                                    if (!assigned && !assigning && event.target.value) {
                                        props?.onAssignSeller(props?.activeBrandRow, event.target.value);
                                    }
                                }}
                            >
                                <option value="">{props?.t('Choose member')}</option>
                                {visibleSellerOptions.map((member) => (
                                    <option
                                        key={member.key}
                                        value={member.value}
                                    >
                                        {member.label}
                                    </option>
                                ))}
                            </select>
                        </Dropdown_>
                    ) : (
                        <Toggle_Stat
                            active={assignedToMe || assigning}
                            text={props?.t('Assign to me')}
                            onClick={() => assignmentLocked ? false : props?.onAssignToMe(props?.activeBrandRow)}
                        />
                    )}
                </Builder_>
                <FormBox>
                    <Builder_ split>
                        <BrandsSliderField
                            label={props?.t('First name')}
                            value={props?.readDisplayValue(props?.activeBrandRow?.firstname, '')}
                        />
                        <BrandsSliderField
                            label={props?.t('Last name')}
                            value={props?.readDisplayValue(props?.activeBrandRow?.lastname, '')}
                        />
                    </Builder_>
                    <Builder_ split>
                        <BrandsSliderField
                            label={props?.t('Brand name')}
                            value={props?.readDisplayValue(props?.activeBrandRow?.brand, props?.t('No brand'))}
                        />
                        <BrandsSliderField
                            label={props?.t('Business')}
                            value={props?.readDisplayValue(props?.activeBrandRow?.actualBusinessName, props?.t('No business'))}
                        />
                    </Builder_>
                    <Builder_ split>
                        <BrandsSliderField
                            label={props?.t('Phone')}
                            value={props?.readDisplayValue(props?.activeBrandRow?.phones, '')}
                        />
                        <BrandsSliderField
                            label={props?.t('Email')}
                            value={props?.readDisplayValue(props?.activeBrandRow?.emails, '')}
                        />
                    </Builder_>
                    <Builder_ split>
                        <BrandsSliderField
                            label={props?.t('Category')}
                            value={props?.readDisplayValue(props?.activeBrandRow?.category, props?.t('No category'))}
                        />
                        <BrandsSliderField
                            label={props?.t('Subcategory')}
                            value={props?.readDisplayValue(props?.activeBrandRow?.subcategory, props?.t('No subcategory'))}
                        />
                    </Builder_>
                    <Builder_ split last>
                        <BrandsSliderField
                            label={props?.t('Location')}
                            value={props?.readDisplayValue(props?.activeBrandRow?.location, '')}
                        />
                        <BrandsSliderField
                            label={props?.t('Client code')}
                            value={props?.readDisplayValue(props?.activeBrandRow?.clientCode, props?.t('No client code'))}
                        />
                    </Builder_>
                </FormBox>
                <BrandsSliderQuests data-testid="brand-quests">
                    <BrandsSliderSelection>
                        <Quest_Provider_Counts brand={props?.activeBrandRow} t={props?.t} />
                    </BrandsSliderSelection>
                </BrandsSliderQuests>
                <Brands_Timeline
                    activeBrandRow={props?.activeBrandRow}
                    t={props?.t}
                />
            </BrandsSliderContent>
        </Slider_>
    );
}

export { Brands_Slider };
